import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Inbox, Trash2, Phone, Users, Building2, MapPin, Megaphone } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";

const statusColor = {
  new: "bg-vteal-light/20 text-vteal-light border-vteal-light/30",
  read: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  resolved: "bg-gray-500/20 text-gray-300 border-gray-500/30",
};
const statusLabel = { new: "Nueva", read: "Leída", resolved: "Atendida" };

export default function PlanRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/plan-requests");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al cargar solicitudes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/plan-requests/${id}`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo actualizar.");
    }
  };
  const del = async (id) => {
    try {
      await api.delete(`/admin/plan-requests/${id}`);
      toast.success("Solicitud eliminada.");
      load();
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  if (loading) return <p className="text-gray-500 py-12 text-center">Cargando...</p>;
  if (items.length === 0)
    return (
      <div className="text-center py-20 text-gray-500">
        <Inbox className="h-10 w-10 mx-auto mb-3 opacity-40" />
        Aún no hay solicitudes de planes.
      </div>
    );

  return (
    <div className="space-y-4" data-testid="plan-requests-list">
      {items.map((r) => (
        <div key={r.id} className="rounded-xl bg-vnavy-800 border border-white/10 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-white font-semibold">{r.name}</h3>
                <Badge className={`border ${statusColor[r.status] || ""}`}>{statusLabel[r.status] || r.status}</Badge>
              </div>
              <p className="text-vteal-light text-sm mt-1">{r.plan}</p>
            </div>
            <span className="text-xs text-gray-600">{new Date(r.created_at).toLocaleString("es-CR")}</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            <Info icon={Building2} label="Empresa" value={r.company || "—"} />
            <Info icon={MapPin} label="Dirección" value={r.address || "—"} />
            <Info icon={null} label="Correo" value={r.email} />
            <Info icon={Phone} label="WhatsApp" value={r.whatsapp || "—"} />
            <Info icon={Users} label="Trabajadores" value={r.employees || "—"} />
            <Info icon={Megaphone} label="¿Cómo nos conoció?" value={r.referral || "—"} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setStatus(r.id, "read")} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-amber-400/40 transition-colors">Marcar leída</button>
            <button onClick={() => setStatus(r.id, "resolved")} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-vteal-light/40 transition-colors">Marcar atendida</button>
            <a href={`mailto:${r.email}`} className="text-xs px-3 py-1.5 rounded-lg bg-vteal-light/15 text-vteal-light hover:bg-vteal-light/25 transition-colors">Responder</a>
            {r.whatsapp && (
              <a href={`https://wa.me/${r.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-lg bg-green-500/15 text-green-300 hover:bg-green-500/25 transition-colors">WhatsApp</a>
            )}
            <button onClick={() => del(r.id)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-red-400 hover:border-red-500/40 transition-colors flex items-center gap-1.5 ml-auto">
              <Trash2 className="h-3.5 w-3.5" /> Eliminar
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-vnavy border border-white/5 px-4 py-2.5">
      <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mb-0.5 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className="text-gray-200 text-sm truncate" title={value}>{value}</p>
    </div>
  );
}
